import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

/**
 * El dinero del negocio, en una sola pantalla.
 *
 * Sustituye a «Ingresos / Egresos», donde había que escribir las dos cosas a mano. Ahora:
 *
 *   · Lo que ENTRA no se escribe: se calcula. Sale de las mensualidades cobradas, las
 *     instalaciones, las ventas y los revendedores. El pago es el ingreso, así que no
 *     pueden discrepar, y una venta anulada deja de sumar sola.
 *   · Lo que SALE sí se escribe, porque no hay ningún documento del que deducirlo.
 *
 * Por eso los ingresos aquí son de solo lectura. No es una limitación: es que el sitio
 * donde se corrige un ingreso es el pago o la venta que lo originó, no esta pantalla.
 */

const ORIGENES = {
  mensualidad:   { icono: '📅', label: 'Mensualidades', color: '#34D399' },
  instalacion:   { icono: '🔧', label: 'Instalaciones', color: '#22D3EE' },
  venta:         { icono: '🛒', label: 'Ventas',        color: '#A5B4FC' },
  revendedor:    { icono: '🤝', label: 'Revendedores',  color: '#FBBF24' },
  manual:        { icono: '✍️', label: 'A mano',        color: '#94A3B8' },
  proveedor_red: { icono: '🌐', label: 'Proveedor de red', color: '#F87171' },
};

const METODOS = ['efectivo', 'nequi', 'daviplata', 'transferencia', 'pse', 'otro'];

const pesos = (n) => '$ ' + Math.round(Number(n) || 0).toLocaleString('es-CO');
const hoyISO = () => new Date().toISOString().slice(0, 10);
const primerDiaMes = () => { const h = new Date(); return new Date(h.getFullYear(), h.getMonth(), 1).toISOString().slice(0, 10); };
const ultimoDiaMes = () => { const h = new Date(); return new Date(h.getFullYear(), h.getMonth() + 1, 0).toISOString().slice(0, 10); };

export default function Dinero() {
  const { usuario } = useAuth();
  const esAdmin = ['admin', 'superadmin'].includes(usuario?.rol);

  const [desde, setDesde] = useState(primerDiaMes());
  const [hasta, setHasta] = useState(ultimoDiaMes());
  const [datos, setDatos] = useState(null);
  const [categorias, setCategorias] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [ver, setVer] = useState('todo');       // todo | ingreso | egreso
  const [form, setForm] = useState(null);        // el gasto que se está escribiendo

  const cargar = useCallback(async () => {
    setCargando(true); setError('');
    try {
      const [m, c] = await Promise.all([
        api.get('/api/transacciones/movimientos', { params: { desde, hasta } }),
        api.get('/api/transacciones/categorias'),
      ]);
      setDatos(m.data);
      setCategorias(c.data || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
      setDatos(null);
    } finally { setCargando(false); }
  }, [desde, hasta]);

  useEffect(() => { cargar(); }, [cargar]);

  const guardarGasto = async () => {
    if (!form.descripcion?.trim() || !(Number(form.monto) > 0)) {
      setMsg('Falta la descripción o el monto'); return;
    }
    try {
      const cuerpo = { ...form, tipo: 'egreso', monto: Number(form.monto) };
      if (form.id) await api.put(`/api/transacciones/${form.id}`, cuerpo);
      else await api.post('/api/transacciones', cuerpo);
      setMsg(form.id ? 'Gasto actualizado' : 'Gasto registrado');
      setForm(null); cargar();
    } catch (e) { setMsg('Error: ' + (e.response?.data?.error || e.message)); }
  };

  const borrarGasto = async (id, concepto) => {
    if (!window.confirm(`¿Borrar el gasto "${concepto}"?`)) return;
    try {
      await api.delete(`/api/transacciones/${id}`);
      setMsg('Gasto borrado'); cargar();
    } catch (e) { setMsg('Error: ' + (e.response?.data?.error || e.message)); }
  };

  const r = datos?.resumen;
  const lista = (datos?.movimientos || []).filter(m => ver === 'todo' || m.tipo === ver);
  const entradas = (datos?.por_origen || []).filter(x => x.tipo === 'ingreso');
  const salidas = (datos?.por_origen || []).filter(x => x.tipo === 'egreso');

  return (
    <Layout>
      <div style={s.pagina}>
        <div style={s.cabecera}>
          <div>
            <h1 style={s.h1}>💰 Dinero</h1>
            <p style={s.sub}>
              Lo que entra lo calcula el sistema de los cobros y las ventas. Los gastos los
              registras tú.
            </p>
          </div>
          <div style={s.fechas}>
            <input type="date" value={desde} onChange={e => setDesde(e.target.value)} style={s.input} />
            <span style={{ color: 'rgba(255,255,255,0.3)' }}>a</span>
            <input type="date" value={hasta} onChange={e => setHasta(e.target.value)} style={s.input} />
          </div>
        </div>

        {msg && <div style={s.msg} onClick={() => setMsg('')}>{msg}</div>}
        {error && <div style={s.error}>{error}</div>}

        {cargando ? <div style={s.vacio}>Cargando…</div> : datos && (
          <>
            <div style={s.tarjetas}>
              <Tarjeta l="Entró" v={pesos(r.ingresos)} color="#34D399" />
              <Tarjeta l="Salió" v={pesos(r.egresos)} color="#F87171" />
              <Tarjeta l="Queda" v={pesos(r.saldo)}
                color={r.saldo >= 0 ? '#A5B4FC' : '#FCA5A5'} grande />
            </div>

            <div style={s.columnas}>
              <div style={s.bloque}>
                <div style={s.tituloBloque}>De dónde vino</div>
                {entradas.length === 0
                  ? <div style={s.nada}>
                      Nada todavía en este periodo. Las mensualidades aparecen aquí solas
                      a medida que las marcas pagadas en <b>Cartera</b>.
                    </div>
                  : entradas.map(x => <Barra key={x.origen} x={x} total={r.ingresos} />)}
              </div>

              <div style={s.bloque}>
                <div style={s.tituloBloque}>
                  En qué se fue
                  {esAdmin && (
                    <button style={s.btnP} onClick={() => setForm({
                      descripcion: '', monto: '', fecha: hoyISO(),
                      categoria_id: '', metodo_pago: 'transferencia' })}>
                      + Registrar gasto
                    </button>
                  )}
                </div>
                {(datos.por_categoria || []).length === 0
                  ? <div style={s.nada}>Sin gastos registrados en este periodo.</div>
                  : datos.por_categoria.map(x => (
                      <Barra key={x.categoria} total={r.egresos} rojo
                        x={{ origen: x.categoria, cuantos: x.cuantos, total: x.total }} />
                    ))}
                {salidas.some(x => x.origen === 'proveedor_red') && (
                  <div style={s.pie}>Incluye lo pagado al proveedor de red.</div>
                )}
              </div>
            </div>

            <div style={s.filtros}>
              {[['todo', 'Todo'], ['ingreso', 'Solo lo que entró'], ['egreso', 'Solo gastos']].map(([k, t]) => (
                <button key={k} onClick={() => setVer(k)}
                  style={{ ...s.chip, ...(ver === k ? s.chipOn : {}) }}>{t}</button>
              ))}
              <span style={s.cuenta}>{lista.length} movimientos</span>
            </div>

            <div style={s.tabla}>
              {lista.length === 0 ? <div style={s.vacio}>Nada en este periodo.</div> : lista.map(m => {
                const o = ORIGENES[m.origen] || { icono: '•', label: m.origen, color: '#94A3B8' };
                const esGastoMio = m.tipo === 'egreso' && m.origen === 'manual';
                return (
                  <div key={m.tipo + m.referencia_id} style={s.fila}>
                    <span style={{ width: '86px', fontSize: '12px', color: 'rgba(255,255,255,0.4)' }}>
                      {m.fecha ? new Date(m.fecha + 'T12:00:00').toLocaleDateString('es-CO',
                        { day: '2-digit', month: 'short' }) : '—'}
                    </span>
                    <span style={{ ...s.etiqueta, color: o.color, borderColor: o.color + '55' }}>
                      {o.icono} {o.label}
                    </span>
                    <span style={s.concepto}>
                      {m.concepto}
                      {m.cliente_nombre && <span style={s.cliente}> · {m.cliente_nombre}</span>}
                      {m.categoria_nombre && <span style={s.cliente}> · {m.categoria_nombre}</span>}
                    </span>
                    <span style={{ width: '92px', fontSize: '11.5px', color: 'rgba(255,255,255,0.35)' }}>
                      {m.metodo_pago || ''}
                    </span>
                    <span style={{ width: '118px', textAlign: 'right', fontWeight: '700',
                      fontFamily: 'monospace', fontSize: '13px',
                      color: m.tipo === 'ingreso' ? '#34D399' : '#F87171' }}>
                      {m.tipo === 'ingreso' ? '' : '−'}{pesos(m.monto)}
                    </span>
                    <span style={{ width: '58px', textAlign: 'right' }}>
                      {/* Solo se editan los gastos escritos a mano. Un ingreso se corrige
                          donde nació: en el pago o en la venta. */}
                      {esAdmin && esGastoMio && (
                        <>
                          <button style={s.mini} onClick={() => setForm({
                            id: m.referencia_id, descripcion: m.concepto, monto: m.monto,
                            fecha: m.fecha, categoria_id: m.categoria_id || '',
                            metodo_pago: m.metodo_pago || 'transferencia' })}>✏️</button>
                          <button style={s.mini}
                            onClick={() => borrarGasto(m.referencia_id, m.concepto)}>✕</button>
                        </>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {form && (
          <div style={s.fondo} onClick={() => setForm(null)}>
            <div style={s.modal} onClick={e => e.stopPropagation()}>
              <h3 style={s.h3}>{form.id ? 'Editar gasto' : 'Registrar un gasto'}</h3>

              <label style={s.lab}>En qué se gastó</label>
              <input autoFocus value={form.descripcion} style={s.input2}
                placeholder="Ej: recibo de luz de la torre Porvenir"
                onChange={e => setForm({ ...form, descripcion: e.target.value })} />

              <div style={{ display: 'flex', gap: '10px' }}>
                <div style={{ flex: 1 }}>
                  <label style={s.lab}>Cuánto</label>
                  <input type="number" value={form.monto} style={s.input2} placeholder="0"
                    onChange={e => setForm({ ...form, monto: e.target.value })} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={s.lab}>Cuándo</label>
                  <input type="date" value={form.fecha} style={s.input2}
                    onChange={e => setForm({ ...form, fecha: e.target.value })} />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <div style={{ flex: 1 }}>
                  <label style={s.lab}>Categoría</label>
                  <select value={form.categoria_id} style={s.input2}
                    onChange={e => setForm({ ...form, categoria_id: e.target.value })}>
                    <option value="">— sin categoría —</option>
                    {categorias.filter(c => c.tipo === 'egreso').map(c => (
                      <option key={c.id} value={c.id}>{c.nombre}</option>
                    ))}
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={s.lab}>Cómo se pagó</label>
                  <select value={form.metodo_pago} style={s.input2}
                    onChange={e => setForm({ ...form, metodo_pago: e.target.value })}>
                    {METODOS.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
                <button style={s.btn} onClick={() => setForm(null)}>Cancelar</button>
                <button style={s.btnP} onClick={guardarGasto}>
                  {form.id ? 'Guardar' : 'Registrar'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

const Tarjeta = ({ l, v, color, grande }) => (
  <div style={{ ...s.tarjeta, ...(grande ? { borderColor: color + '44' } : {}) }}>
    <div style={{ fontSize: '11.5px', color: 'rgba(255,255,255,0.45)', marginBottom: '4px' }}>{l}</div>
    <div style={{ fontSize: grande ? '24px' : '20px', fontWeight: '700', color }}>{v}</div>
  </div>
);

// Una barra proporcional dice de un vistazo cuánto pesa cada fuente. Un número suelto no.
const Barra = ({ x, total, rojo }) => {
  const o = ORIGENES[x.origen] || { icono: '•', label: x.origen, color: rojo ? '#F87171' : '#94A3B8' };
  const pct = total > 0 ? Math.round((x.total / total) * 100) : 0;
  return (
    <div style={{ marginBottom: '9px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12.5px', marginBottom: '3px' }}>
        <span style={{ color: 'rgba(255,255,255,0.75)' }}>
          {ORIGENES[x.origen] ? `${o.icono} ${o.label}` : x.origen}
          <span style={{ color: 'rgba(255,255,255,0.3)' }}> · {x.cuantos}</span>
        </span>
        <span style={{ fontFamily: 'monospace', color: o.color }}>{pesos(x.total)}</span>
      </div>
      <div style={s.barraFondo}>
        <div style={{ ...s.barra, width: pct + '%', background: o.color }} />
      </div>
    </div>
  );
};

const s = {
  pagina:   { padding: '22px 26px' },
  cabecera: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap', marginBottom: '16px' },
  h1:       { fontSize: '20px', fontWeight: '700', color: '#fff', marginBottom: '3px' },
  sub:      { fontSize: '13px', color: 'rgba(255,255,255,0.45)', maxWidth: '520px', lineHeight: '1.5' },
  fechas:   { display: 'flex', flexWrap: 'wrap', gap: '7px', alignItems: 'center' },
  input:    { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '8px', padding: '7px 10px', color: '#fff', fontSize: '12.5px' },
  tarjetas: { display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '18px' },
  tarjeta:  { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '11px', padding: '13px 18px', minWidth: '150px' },
  columnas: { display: 'flex', gap: '14px', flexWrap: 'wrap', marginBottom: '18px' },
  bloque:   { flex: '1 1 320px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '11px', padding: '14px 16px' },
  tituloBloque: { fontSize: '12.5px', fontWeight: '700', color: '#A5B4FC', marginBottom: '11px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' },
  barraFondo: { height: '5px', background: 'rgba(255,255,255,0.06)', borderRadius: '3px', overflow: 'hidden' },
  barra:    { height: '100%', borderRadius: '3px' },
  nada:     { fontSize: '12px', color: 'rgba(255,255,255,0.35)', lineHeight: '1.6' },
  pie:      { fontSize: '11px', color: 'rgba(255,255,255,0.3)', marginTop: '8px' },
  filtros:  { display: 'flex', gap: '7px', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap' },
  chip:     { padding: '6px 13px', borderRadius: '7px', fontSize: '12.5px', cursor: 'pointer', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)' },
  chipOn:   { background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.5)', color: '#A5B4FC', fontWeight: '600' },
  cuenta:   { fontSize: '11.5px', color: 'rgba(255,255,255,0.3)', marginLeft: 'auto' },
  tabla:    { background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '10px', overflow: 'hidden' },
  fila:     { display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 13px', borderBottom: '1px solid rgba(255,255,255,0.04)' },
  etiqueta: { width: '128px', fontSize: '11px', padding: '2px 7px', borderRadius: '5px', border: '1px solid', flexShrink: 0, textAlign: 'center' },
  concepto: { flex: 1, minWidth: 0, fontSize: '12.5px', color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  cliente:  { color: 'rgba(255,255,255,0.38)' },
  vacio:    { padding: '34px', textAlign: 'center', color: 'rgba(255,255,255,0.35)', fontSize: '13px' },
  msg:      { background: 'rgba(52,211,153,0.1)', border: '1px solid rgba(52,211,153,0.3)', borderRadius: '9px', padding: '10px 13px', marginBottom: '12px', color: '#6EE7B7', fontSize: '13px', cursor: 'pointer' },
  error:    { background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '9px', padding: '11px', marginBottom: '12px', color: '#FCA5A5', fontSize: '13px' },
  fondo:    { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.66)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 900, padding: '18px' },
  modal:    { background: '#131722', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '13px', padding: '20px', width: '100%', maxWidth: '440px' },
  h3:       { fontSize: '15px', fontWeight: '700', color: '#fff', marginBottom: '14px' },
  lab:      { display: 'block', fontSize: '11.5px', color: 'rgba(255,255,255,0.45)', marginBottom: '4px', marginTop: '10px' },
  input2:   { width: '100%', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '8px', padding: '9px 11px', color: '#fff', fontSize: '13px' },
  btn:      { padding: '8px 15px', borderRadius: '8px', fontSize: '13px', cursor: 'pointer', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.6)' },
  btnP:     { padding: '7px 14px', borderRadius: '8px', fontSize: '12.5px', cursor: 'pointer', background: 'rgba(99,102,241,0.2)', border: '1px solid rgba(99,102,241,0.5)', color: '#A5B4FC', fontWeight: '600' },
  mini:     { background: 'none', border: 'none', color: 'rgba(255,255,255,0.35)', cursor: 'pointer', fontSize: '11px', padding: '2px 3px' },
};
